import { CalendarCheck, CalendarPlus, Check, ExternalLink, MoveRight, Plus, Repeat, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCalendarFlow } from "@/components/calendar/calendar-flow";
import {
  formatDay,
  formatMonthDay,
  formatTime,
  minutesOf,
  nextEvent,
  scheduleChipLabel,
  upcomingEvents,
} from "@/lib/calendar";
import type { MomentumItem } from "@/lib/momentum";
import { cn } from "@/lib/utils";

const itemClass =
  "flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-[14px] text-foreground outline-none focus:bg-foreground/[0.04] data-[highlighted]:bg-foreground/[0.04]";

/**
 * A step's calendar line on its Momentum card: "Add to Calendar" until it's scheduled,
 * then a date chip (or a rhythm chip) that opens View in Google Calendar, Reschedule,
 * Add again or Remove. A completed step keeps its date as a green record.
 */
export function StepCalendarAction({ item }: { item: MomentumItem }) {
  const flow = useCalendarFlow();
  const schedule = flow.schedules.get(item.id);
  const step = { id: item.id, journalId: item.journalId, text: item.text };

  if (!schedule || schedule.events.length === 0) {
    if (item.completed) return null;
    const tip = flow.connection ? "Opens Find a time · Google Calendar connected" : "Add this step to your calendar";
    return (
      <Tooltip delayDuration={250}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => flow.addToCalendar(step)}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-primary/80 transition-colors hover:bg-primary/[0.07] hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <CalendarPlus className="h-3.5 w-3.5" />
            Add to Calendar
          </button>
        </TooltipTrigger>
        {flow.available && (
          <TooltipContent side="bottom" className="z-[70] rounded-lg bg-foreground px-2.5 py-1.5 text-xs text-white">
            {tip}
          </TooltipContent>
        )}
      </Tooltip>
    );
  }

  const next = nextEvent(schedule);
  const upcoming = upcomingEvents(schedule);
  const isRhythm = schedule.kind === "rhythm" && !!schedule.rhythm;
  const last = [...schedule.events].sort((a, b) => b.start.localeCompare(a.start))[0];

  const chip = item.completed ? (
    <>
      <Check className="h-3.5 w-3.5" />
      Done{next ? ` · ${formatDay(new Date(next.start))}` : ""}
    </>
  ) : (
    <>
      {isRhythm ? <Repeat className="h-3.5 w-3.5" /> : <CalendarCheck className="h-3.5 w-3.5" />}
      {scheduleChipLabel(schedule)}
      {!isRhythm && upcoming.length > 1 && <span className="text-primary/60">· +{upcoming.length - 1}</span>}
    </>
  );

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Calendar options for ${item.text}`}
          className={cn(
            "ml-2 inline-flex max-w-full items-center gap-1.5 truncate rounded-full px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            item.completed
              ? "bg-[#e2efe4] text-[#2f6b45] hover:bg-[#d5e8d8]"
              : "bg-primary/10 text-primary hover:bg-primary/15 data-[state=open]:bg-primary/15",
          )}
        >
          {chip}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="z-[70] w-[248px] rounded-2xl border border-foreground/[0.06] bg-white p-1.5 text-foreground shadow-[0_16px_40px_-12px_rgba(47,41,37,0.28)]"
      >
        {isRhythm && next && !item.completed && (
          <>
            <DropdownMenuLabel className="px-3 pb-2 pt-2 font-normal">
              <span className="block text-[13.5px] font-semibold text-foreground">
                Next: {formatDay(new Date(next.start))} · {formatTime(minutesOf(new Date(next.start)))}
              </span>
              <span className="block text-xs text-muted-foreground">
                {upcoming.length} {upcoming.length === 1 ? "time" : "times"}
                {schedule.rhythm?.weeks === null ? ", ongoing" : last ? ` through ${formatMonthDay(new Date(last.start))}` : ""}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="mx-1 bg-foreground/[0.06]" />
          </>
        )}
        {!isRhythm && upcoming.length > 1 && !item.completed && (
          <>
            <DropdownMenuLabel className="px-3 pb-2 pt-2 text-xs font-normal text-muted-foreground">
              {upcoming.length} events · next {next ? formatDay(new Date(next.start)) : ""}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="mx-1 bg-foreground/[0.06]" />
          </>
        )}
        {next && (
          <DropdownMenuItem asChild className={itemClass}>
            <a href={next.htmlLink} target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4 text-foreground/70" />
              View in Google Calendar
            </a>
          </DropdownMenuItem>
        )}
        {!item.completed && flow.connection && (
          <>
            {isRhythm ? (
              <DropdownMenuItem className={itemClass} onSelect={() => flow.plan(step, "rhythm")}>
                <Repeat className="h-4 w-4 text-foreground/70" />
                Change the rhythm
              </DropdownMenuItem>
            ) : (
              <>
                {upcoming.length > 0 && (
                  <DropdownMenuItem className={itemClass} onSelect={() => flow.plan(step, "reschedule")}>
                    <MoveRight className="h-4 w-4 text-foreground/70" />
                    Reschedule
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem className={itemClass} onSelect={() => flow.plan(step, "again")}>
                  <Plus className="h-4 w-4 text-foreground/70" />
                  Add again
                </DropdownMenuItem>
              </>
            )}
            {upcoming.length > 0 && (
              <>
                <DropdownMenuSeparator className="mx-1 bg-foreground/[0.06]" />
                <DropdownMenuItem
                  className={cn(itemClass, "text-[#a8323e] focus:bg-[#fbe6e6]/60 data-[highlighted]:bg-[#fbe6e6]/60")}
                  onSelect={() => flow.remove(step)}
                >
                  <Trash2 className="h-4 w-4" />
                  Remove from calendar
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
